"use client"

import { useEffect, useRef, useState } from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { useLocation } from "react-router-dom"
import { MAIN_URL, getApiHeaders } from "../../../Configurations/Urls"
import useAuthStore from "../../../Zustand/Store/useAuthStore"
import {
  resolveOrganizationFromUserData,
  resolveOrganizationId,
} from "../../../lib/organizationSession"

function capitalizeWords(str) {
  if (!str || typeof str !== "string") return "N/A"
  return str.replace(/\b\w/g, (char) => char.toUpperCase())
}

function display(value, fallback = "N/A") {
  if (value === null || value === undefined || value === "") return fallback
  if (typeof value === "string") {
    const trimmed = value.trim()
    return trimmed ? capitalizeWords(trimmed) : fallback
  }
  return value
}

/** First non-empty string from candidate values (API key variants). */
function firstText(...candidates) {
  for (const value of candidates) {
    if (value === null || value === undefined) continue
    const text = String(value).trim()
    if (text && text !== "-" && text.toLowerCase() !== "n/a") return text
  }
  return null
}

function formatDate(dateStr) {
  if (!dateStr) return "N/A"
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return "N/A"
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })
}

function calculateAge(dob) {
  if (!dob) return null
  const birthDate = new Date(dob)
  if (isNaN(birthDate.getTime())) return null
  const today = new Date()
  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDiff = today.getMonth() - birthDate.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--
  }
  return age >= 0 ? age : null
}

function formatTime24to12(time24) {
  if (!time24 || typeof time24 !== "string") return null
  const [hh, mm] = time24.split(":")
  const hour = Number.parseInt(hh, 10)
  if (isNaN(hour) || !mm) return null
  const ampm = hour >= 12 ? "PM" : "AM"
  const hour12 = hour % 12 || 12
  return `${hour12}:${mm} ${ampm}`
}

function getTenureSinceJoining(joinDateStr) {
  if (!joinDateStr) return "N/A"
  const joinDate = new Date(joinDateStr)
  if (isNaN(joinDate.getTime())) return "N/A"

  const today = new Date()
  let years = today.getFullYear() - joinDate.getFullYear()
  let months = today.getMonth() - joinDate.getMonth()
  let days = today.getDate() - joinDate.getDate()

  if (days < 0) {
    months--
    const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0)
    days += prevMonth.getDate()
  }
  if (months < 0) {
    years--
    months += 12
  }

  if (years > 0 && months > 0) {
    return `${years} Year${years > 1 ? "s" : ""} ${months} Month${months > 1 ? "s" : ""}`
  }
  if (years > 0) return `${years} Year${years > 1 ? "s" : ""}`
  if (months > 0) return `${months} Month${months > 1 ? "s" : ""}`
  if (days > 1) return `${days} Days`
  if (days === 1) return "1 Day"
  if (days === 0) return "Today"
  return "N/A"
}

function calcExperienceSpan(startDateStr, endDateStr = null) {
  if (!startDateStr) return null
  const start = new Date(startDateStr)
  if (isNaN(start.getTime())) return null
  const end = endDateStr ? new Date(endDateStr) : new Date()
  if (isNaN(end.getTime())) return null

  let years = end.getFullYear() - start.getFullYear()
  let months = end.getMonth() - start.getMonth()
  if (months < 0) {
    years--
    months += 12
  }
  if (years <= 0 && months <= 0) return "Less than a month"
  if (years > 0 && months > 0) return `${years} Years ${months} Months`
  if (years > 0) return `${years} Years`
  return `${months} Months`
}

function employeeInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
  if (parts.length >= 2) {
    return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase()
  }
  return (parts[0] || "E").slice(0, 1).toUpperCase()
}

function familyMemberAge(member) {
  if (member?.age != null && member.age !== "") return member.age
  return calculateAge(member?.date_of_birth) ?? "—"
}

const EmployeePagination = ({
  currentEmployee,
  totalEmployees,
  onPrevious,
  onNext,
  canNavigatePrev,
  canNavigateNext,
}) => (
  <div className="ems-ep-nav">
    <span className="ems-ep-nav-count">
      Employee {currentEmployee} of {totalEmployees}
    </span>
    <button type="button" className="ems-btn ems-ghost" onClick={onPrevious} disabled={!canNavigatePrev}>
      <ChevronLeft size={16} />
      Prev Emp
    </button>
    <button type="button" className="ems-btn ems-ghost" onClick={onNext} disabled={!canNavigateNext}>
      Next Emp
      <ChevronRight size={16} />
    </button>
  </div>
)

function DetailItem({ label, value }) {
  return (
    <div className="ems-ep-item">
      {label ? <div className="ems-ep-k">{label}</div> : null}
      <div className="ems-ep-v">{value ?? "N/A"}</div>
    </div>
  )
}

function SnapshotField({ label, value }) {
  return (
    <div className="ems-ep-snap-field">
      <span className="ems-ep-snap-label">{label}</span>
      <span className="ems-ep-snap-sep" aria-hidden="true">
        :
      </span>
      <span className="ems-ep-snap-value">{value ?? "N/A"}</span>
    </div>
  )
}

function SnapshotGroup({ children }) {
  return <div className="ems-ep-snap-group">{children}</div>
}

const ProfileImageCard = ({ imageUrl, name, designation, employeeCode }) => {
  const src = typeof imageUrl === "string" && imageUrl !== "N/A" ? imageUrl : ""
  const label = typeof name === "string" ? name : ""
  return (
    <div className="ems-card ems-ep-photo">
      <div className="ems-ep-avatar" aria-label={label || "Employee"}>
        {src ? <img src={src} alt={label || "Employee"} /> : employeeInitials(label)}
      </div>
      <div className="ems-ep-photo-meta">
        <h2 className="ems-ep-photo-name">{display(label)}</h2>
        <p className="ems-ep-photo-desig">{display(designation)}</p>
        <p className="ems-ep-photo-code">
          Emp Code: {display(employeeCode)}
        </p>
      </div>
    </div>
  )
}

/** Top identity — left column = role snapshot groups; right = tenure/pay/contact */
const SnapshotHeroCard = ({ employee }) => {
  const workshiftValue =
    employee.workshiftHours && employee.workshiftHours !== "N/A"
      ? employee.workshiftHours
      : display(employee.workshiftName)

  return (
    <div className="ems-card ems-ep-identity ems-ep-snapshot-hero">
      <div className="ems-ep-snap-cols">
        <div className="ems-ep-snap-col">
          <SnapshotGroup>
            <SnapshotField label="Department" value={display(employee.department)} />
            <SnapshotField label="Level" value={display(employee.level)} />
          </SnapshotGroup>

          <SnapshotGroup>
            <SnapshotField label="Employment Type" value={display(employee.employmentType)} />
            <SnapshotField label="Employment Stage" value={display(employee.employmentStage)} />
          </SnapshotGroup>

          <SnapshotGroup>
            <SnapshotField label="Emp Category" value={display(employee.employmentCategory)} />
            <SnapshotField label="Workshift" value={workshiftValue} />
            <SnapshotField label="Work Model" value={display(employee.workModel)} />
            <SnapshotField label="Recruitment Source" value={display(employee.recruitmentSource)} />
          </SnapshotGroup>
        </div>

        <div className="ems-ep-snap-col ems-ep-snap-col-right">
          <SnapshotGroup>
            <SnapshotField label="Total Experience" value={employee.totalExperience || "N/A"} />
            <SnapshotField
              label="Working us since"
              value={
                employee.dateOfJoining && employee.dateOfJoining !== "N/A"
                  ? `${employee.withUsFor || "N/A"} · ${employee.dateOfJoining}`
                  : employee.withUsFor || "N/A"
              }
            />
          </SnapshotGroup>

          <SnapshotGroup>
            <SnapshotField label="Current CTC" value={employee.currentCtc || "N/A"} />
            <SnapshotField label="Last Increment" value={employee.lastIncrementDisplay || "N/A"} />
          </SnapshotGroup>

          <SnapshotGroup>
            <SnapshotField label="Reporting To" value={display(employee.manager)} />
            <SnapshotField
              label="Reporting Team Size"
              value={
                employee.reportingTeamSize != null
                  ? `${employee.reportingTeamSize} Member${Number(employee.reportingTeamSize) === 1 ? "" : "s"}`
                  : "N/A"
              }
            />
          </SnapshotGroup>

          <SnapshotGroup>
            <SnapshotField label="Work Email" value={employee.workEmail || "N/A"} />
            <SnapshotField label="Work Phone Number" value={employee.workPhone || "N/A"} />
          </SnapshotGroup>
        </div>
      </div>
    </div>
  )
}

const PersonalEducationCard = ({ employee }) => (
  <div className="ems-card ems-ep-card">
    <h3 className="ems-ep-card-title">Personal &amp; Education</h3>
    <div className="ems-ep-kv">
      <DetailItem
        label="Age | Blood Group | Gender"
        value={`${employee.age != null ? `${employee.age} Years` : "N/A"} · ${
          employee.bloodGroup || "N/A"
        } · ${display(employee.gender)}`}
      />
      <DetailItem label="Marital Status" value={display(employee.maritalStatus)} />
      <DetailItem label="DOB" value={employee.dob || "N/A"} />
      <DetailItem
        label="Education (Year of passing)"
        value={
          employee.educationYear && employee.educationYear !== "N/A"
            ? `${display(employee.education)} (${employee.educationYear})`
            : display(employee.education)
        }
      />
      <DetailItem label="College" value={display(employee.college)} />
      <DetailItem label="Languages" value={employee.languages || "N/A"} />
      <DetailItem label="Personal Email" value={employee.personalEmail || "N/A"} />
      <DetailItem label="Personal Phone" value={employee.personalPhone || "N/A"} />
      <DetailItem label="Emergency Contact" value={employee.emergencyContact || "N/A"} />
    </div>
  </div>
)

const FamilyDetailsCard = ({ employee }) => {
  const members = Array.isArray(employee.familyMembersList) ? employee.familyMembersList : []

  return (
    <div className="ems-card ems-ep-card">
      <h3 className="ems-ep-card-title">Family</h3>
      <div className="ems-ep-kv">
        <div className="ems-ep-item">
          <div className="ems-ep-k">Family details</div>
          {members.length > 0 ? (
            <table className="ems-ep-mini-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Age</th>
                  <th>Occupation</th>
                </tr>
              </thead>
              <tbody>
                {members.map((member, index) => (
                  <tr key={`fam-${index}`} className={member.isDependent ? "is-dependent" : undefined}>
                    <td>{index + 1}.</td>
                    <td>
                      <span className="ems-ep-fam-name">
                        {display(member.name)}
                        {member.relation && member.relation !== "N/A"
                          ? ` (${display(member.relation)})`
                          : ""}
                      </span>
                      {member.isDependent ? (
                        <span className="ems-ep-dep-badge" title="Dependent">
                          Dependent
                        </span>
                      ) : null}
                    </td>
                    <td>{member.age ?? "—"}</td>
                    <td>{display(member.occupation, "—")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="ems-ep-v">No family details</div>
          )}
        </div>

        <DetailItem label="Housing Status" value={display(employee.housingStatus)} />
        <DetailItem label="Address" value={employee.fullAddress || "N/A"} />
      </div>
    </div>
  )
}

const AttendanceCard = ({ employee }) => {
  const present = employee.attendancePresent
  const working = employee.attendanceWorking
  const monthLabel = employee.attendanceMonthLabel
  const ratio =
    present != null && working != null ? `${present} / ${working}` : null
  const currentMonthValue = ratio
    ? monthLabel
      ? `${monthLabel} · ${ratio}`
      : ratio
    : employee.attendanceSummary || "N/A"

  const leaveDays = employee.currentMonthLeaveDays
  const leavesValue =
    leaveDays != null
      ? `${leaveDays} day${Number(leaveDays) === 1 ? "" : "s"}`
      : employee.leaves || "N/A"

  return (
    <div className="ems-card ems-ep-card">
      <h3 className="ems-ep-card-title">Attendance</h3>
      <div className="ems-ep-kv">
        <DetailItem label="Current Month" value={currentMonthValue} />
        <DetailItem
          label={monthLabel ? `Leaves (${monthLabel})` : "Leaves"}
          value={leavesValue}
        />
      </div>
    </div>
  )
}

const FunctionalRoleCard = ({ employee }) => {
  const roles = Array.isArray(employee.functionalRolesList)
    ? employee.functionalRolesList
    : []

  return (
    <div className="ems-card ems-ep-card">
      <h3 className="ems-ep-card-title">Functional Role</h3>
      <div className="ems-ep-kv">
        <div className="ems-ep-item">
          <div className="ems-ep-k">Roles & specialization</div>
          {roles.length > 0 ? (
            <table className="ems-ep-mini-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Role</th>
                  <th>Specialization</th>
                </tr>
              </thead>
              <tbody>
                {roles.map((role, index) => (
                  <tr key={`fr-${index}`}>
                    <td>{index + 1}.</td>
                    <td>
                      <span className="ems-ep-fam-name">{display(role.role)}</span>
                      {role.isPrimary ? (
                        <span className="ems-ep-dep-badge" title="Primary role">
                          Primary
                        </span>
                      ) : null}
                    </td>
                    <td>{display(role.specialization)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="ems-ep-v">No functional roles</div>
          )}
        </div>
      </div>
    </div>
  )
}

function mapEmployeeData(emp, userData) {
  const person = emp?.org_persons?.person || emp?.OrgPersons?.Person || {}
  const orgPerson = emp?.org_persons || emp?.OrgPersons || {}
  const contact = emp?.contact || {}
  const address = emp?.address || {}
  const medical = emp?.medical || emp?.Medical || {}

  const deptLoc = Array.isArray(emp?.department_location)
    ? emp.department_location[0]
    : Array.isArray(emp?.departmentLocation)
      ? emp.departmentLocation[0]
      : emp?.department_location || emp?.departmentLocation || null

  const departmentName =
    deptLoc?.department?.department_name ||
    emp?.designation?.department?.department_name ||
    "N/A"

  const locationName =
    deptLoc?.location?.location_name ||
    address?.city?.city_name ||
    address?.city?.name ||
    "N/A"

  const fullAddress = [
    address?.address_line1,
    address?.address_line2,
    address?.address_line3,
    address?.city?.city_name || address?.city?.name,
  ]
    .filter(Boolean)
    .join(", ")

  // Prefer office/work location name; never dump full home address into hero Location
  const workLocation =
    deptLoc?.location?.location_name ||
    emp?.organizationunit?.unit_name ||
    address?.city?.city_name ||
    address?.city?.name ||
    "N/A"

  const workshift = emp?.workshift || {}
  const start = formatTime24to12(workshift.work_shift_start_time)
  const end = formatTime24to12(workshift.work_shift_end_time)
  const workshiftHours = start && end ? `${start} - ${end}` : "N/A"
  const workshiftName = workshift.work_shift_name || workshift.workshift_name || "N/A"

  const dobRaw = person?.date_of_birth || emp?.date_of_birth
  const age = calculateAge(dobRaw)
  const joinDate = emp?.date_of_joining || orgPerson?.organization_join_date

  const experiences = Array.isArray(emp?.experience)
    ? emp.experience
    : emp?.experience
      ? [emp.experience]
      : []

  let totalExperience = "N/A"
  if (experiences.length > 0) {
    const earliest = experiences
      .map((e) => e?.start_date)
      .filter(Boolean)
      .sort()[0]
    totalExperience = calcExperienceSpan(earliest) || "N/A"
  } else if (joinDate) {
    totalExperience = getTenureSinceJoining(joinDate)
  }

  const organizationsCount = new Set(
    experiences.map((exp) => exp?.organization_name).filter(Boolean)
  ).size

  // Current CTC from employee_increments (latest new_ctc_amount) — NOT experience compensation
  const increments = Array.isArray(emp?.increments)
    ? emp.increments
    : Array.isArray(emp?.employee_increments)
      ? emp.employee_increments
      : []
  const sortedIncrements = [...increments].sort((a, b) => {
    const da = new Date(a?.effective_date || a?.increment_date || a?.created_at || 0).getTime()
    const db = new Date(b?.effective_date || b?.increment_date || b?.created_at || 0).getTime()
    return db - da
  })
  const latestIncrement = sortedIncrements[0] || null

  const formatInr = (amount) => {
    if (amount === null || amount === undefined || amount === "") return null
    const num = Number(amount)
    if (!Number.isFinite(num)) return null
    return `₹${num.toLocaleString("en-IN")}`
  }

  const currentCtc =
    formatInr(emp?.current_ctc_amount ?? latestIncrement?.new_ctc_amount) || "N/A"

  const lastIncrementAmount = formatInr(
    emp?.last_increment_amount ?? latestIncrement?.increment_amount
  )
  const lastIncrementDate = formatDate(
    emp?.last_increment_date || latestIncrement?.effective_date || latestIncrement?.increment_date
  )
  let lastIncrementDisplay = "N/A"
  if (lastIncrementAmount && lastIncrementDate !== "N/A") {
    lastIncrementDisplay = `${lastIncrementAmount} on ${lastIncrementDate}`
  } else if (lastIncrementAmount) {
    lastIncrementDisplay = lastIncrementAmount
  } else if (lastIncrementDate !== "N/A") {
    lastIncrementDisplay = lastIncrementDate
  }

  const reportingTeamSize =
    typeof emp?.reporting_team_size === "number"
      ? emp.reporting_team_size
      : Array.isArray(emp?.direct_reports)
        ? emp.direct_reports.length
        : Array.isArray(emp?.directReports)
          ? emp.directReports.length
          : 0

  const familyRaw = Array.isArray(emp?.family)
    ? emp.family
    : Array.isArray(emp?.family_members)
      ? emp.family_members
      : emp?.family
        ? [emp.family]
        : []

  const familyMembersList = familyRaw.map((member) => ({
    name: member?.name || "N/A",
    relation: member?.relationship || member?.relation || "N/A",
    age: familyMemberAge(member),
    occupation: member?.occupation_details || member?.occupation || "N/A",
    isDependent: Boolean(member?.is_dependent || member?.dependent),
  }))

  const educationList = Array.isArray(emp?.education)
    ? emp.education
    : Array.isArray(emp?.Education)
      ? emp.Education
      : []

  const latestEducation = educationList.length > 0 ? educationList[educationList.length - 1] : null
  let educationName = "N/A"
  if (latestEducation) {
    const degree =
      latestEducation?.degree?.[0]?.education_degree_name ||
      latestEducation?.degree?.education_degree_name ||
      null
    educationName = degree || latestEducation?.institute_name || "N/A"
  }

  const languageRows = Array.isArray(emp?.languages) ? emp.languages : []
  const languageNames = []
  const seenLanguages = new Set()
  for (const l of languageRows) {
    const name =
      l?.language_name ||
      l?.language?.language_name ||
      l?.organization_language?.language_name ||
      null
    if (!name) continue
    const key = String(name).trim().toLowerCase()
    if (!key || seenLanguages.has(key)) continue
    seenLanguages.add(key)
    languageNames.push(String(name).trim())
  }
  const languagesLabel = languageNames.length > 0 ? languageNames.join(", ") : "N/A"

  const functionalRoles = Array.isArray(emp?.functional_roles)
    ? emp.functional_roles
    : Array.isArray(emp?.functionalRoles)
      ? emp.functionalRoles
      : []

  const functionalRolesList = functionalRoles
    .map((r) => {
      const role =
        r?.function_role?.functional_role_name ||
        r?.functionRole?.functional_role_name ||
        r?.functional_role_name ||
        null
      const specialization =
        r?.role_specialization?.functional_role_specialization_name ||
        r?.RoleSpecialization?.functional_role_specialization_name ||
        r?.functional_role_specialization_name ||
        null
      if (!role && !specialization) return null
      return {
        role: role || "N/A",
        specialization: specialization || "N/A",
        isPrimary: Boolean(r?.is_primary || r?.isPrimary),
      }
    })
    .filter(Boolean)

  // Primary first, then remaining roles
  functionalRolesList.sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary))

  const primaryRole =
    functionalRolesList.find((r) => r.isPrimary) || functionalRolesList[0] || null
  const functionalRoleName = primaryRole?.role || "N/A"
  const functionalSpecialization = primaryRole?.specialization || "N/A"

  const leaveBalances = Array.isArray(emp?.leave_balances)
    ? emp.leave_balances
    : Array.isArray(emp?.leaveBalances)
      ? emp.leaveBalances
      : []

  const currentMonthLeaveDays =
    emp?.current_month_leave_days != null
      ? Number(emp.current_month_leave_days)
      : null

  const leaveParts = leaveBalances
    .map((lb) => {
      const taken = Number(lb?.leave_taken_days ?? 0)
      if (!taken) return null
      const typeName =
        lb?.leave_type?.leave_type_name ||
        lb?.leaveType?.leave_type_name ||
        "leave"
      return `${taken} ${String(typeName).toLowerCase()}`
    })
    .filter(Boolean)
  const leavesLabel =
    currentMonthLeaveDays != null
      ? `${currentMonthLeaveDays} day${currentMonthLeaveDays === 1 ? "" : "s"}`
      : leaveParts.length > 0
        ? `(${leaveParts.join(" · ")})`
        : "No leaves recorded"

  const attendanceTypeRaw =
    emp?.today_latest_attendance?.attendance_log_type ??
    emp?.TodayLatestAttendance?.attendance_log_type ??
    emp?.today_latest_attendance?.log_type ??
    ""
  const attendanceTypeLower =
    typeof attendanceTypeRaw === "string" ? attendanceTypeRaw.toLowerCase() : ""
  const isClockedIn =
    attendanceTypeLower.includes("clock in") ||
    attendanceTypeLower.includes("break start") ||
    attendanceTypeLower.includes("break end")
  const isClockedOut = attendanceTypeLower.includes("clock out")

  let profileImageUrl =
    person?.profile_image_url || emp?.profile_image_url || orgPerson?.person?.profile_image_url || ""
  if (
    profileImageUrl &&
    !String(profileImageUrl).startsWith("http://") &&
    !String(profileImageUrl).startsWith("https://")
  ) {
    profileImageUrl = `${MAIN_URL}/storage/app/public/employees/${profileImageUrl}`
  }

  const managerPerson =
    emp?.manager?.org_persons?.person ||
    emp?.manager?.OrgPersons?.Person ||
    null
  const managerName =
    emp?.reporting_manager_name ||
    managerPerson?.full_name ||
    [managerPerson?.first_name, managerPerson?.middle_name, managerPerson?.last_name]
      .filter(Boolean)
      .join(" ") ||
    [emp?.manager?.first_name, emp?.manager?.middle_name, emp?.manager?.last_name]
      .filter(Boolean)
      .join(" ") ||
    "N/A"

  const orgRecord = resolveOrganizationFromUserData(userData)
  const organizationName =
    emp?.organization?.organization_name ||
    orgRecord?.organization_name ||
    "N/A"

  const emergencyNameParts = [
    contact?.emergency_person_name_1,
    contact?.emergency_person_relation_1
      ? `(${contact.emergency_person_relation_1})`
      : null,
  ].filter(Boolean)
  const emergencyPhone = contact?.emergency_person_phone_number_1 || null
  const emergencyContact =
    emergencyNameParts.length && emergencyPhone
      ? `${emergencyNameParts.join(" ")}  ·  ${emergencyPhone}`
      : emergencyNameParts.length
        ? emergencyNameParts.join(" ")
        : emergencyPhone || "N/A"

  return {
    imageUrl: profileImageUrl || "",
    name: person?.full_name || emp?.full_name || "N/A",
    employeeCode:
      firstText(
        emp?.employee_code,
        emp?.Employee_Code,
        emp?.emp_code,
        emp?.code,
        emp?.person_code,
        emp?.org_persons?.person_code,
        emp?.OrgPersons?.person_code,
        emp?.org_persons?.person?.person_code
      ) || "N/A",
    designation: emp?.designation?.designation_name || "N/A",
    level: emp?.designation?.level?.level_name || "N/A",
    department: departmentName,
    location: locationName,
    workLocation,
    manager: managerName || "N/A",
    workshiftName,
    workshiftHours,
    employmentType:
      firstText(
        emp?.employemnettype?.employment_type_name,
        emp?.employment_type_name,
        emp?.employment_type
      ) || "N/A",
    employmentStage:
      firstText(
        emp?.employment_stage_name,
        emp?.employment_stage,
        emp?.stages?.employment_stage_name,
        emp?.Stages?.employment_stage_name,
        emp?.stages?.name,
        emp?.organization_employment_stage?.employment_stage_name
      ) || "N/A",
    employmentCategory:
      firstText(
        emp?.employment_category_name,
        emp?.employment_category,
        emp?.category?.employment_category_name,
        emp?.Category?.employment_category_name,
        emp?.category?.name,
        emp?.organization_employment_category?.employment_category_name
      ) || "N/A",
    workModel:
      firstText(emp?.workmodel?.work_model_name, emp?.work_model_name) || "N/A",
    recruitmentSource:
      firstText(
        emp?.recruitment_source_name,
        emp?.recruitment_source?.recruitment_source_name,
        emp?.organization_recruitment_source?.recruitment_source_name,
        emp?.recruitmentSource?.recruitment_source_name
      ) || "N/A",
    withUsFor: getTenureSinceJoining(joinDate),
    dateOfJoining: formatDate(joinDate),
    totalExperience,
    organizations: organizationsCount || "",
    currentCtc,
    lastIncrement: lastIncrementAmount || "N/A",
    lastIncrementDisplay,
    incrementDate: lastIncrementDate,
    reportingTeamSize,
    workEmail: contact?.work_email || "N/A",
    workPhone: contact?.work_phone_number || "N/A",
    personalEmail: contact?.personal_email || "N/A",
    personalPhone: contact?.personal_phone_number || "N/A",
    emergencyContact,
    age,
    bloodGroup: medical?.blood_group || "N/A",
    gender: person?.gender || emp?.gender || "N/A",
    maritalStatus: person?.marital_status || emp?.marital_status || "N/A",
    dob: formatDate(dobRaw),
    education: educationName,
    educationYear: latestEducation?.year_of_passing || "N/A",
    college: latestEducation?.institute_name || "N/A",
    languages: languagesLabel,
    familyMembers: familyMembersList.length,
    dependents: familyMembersList.filter((m) => m.isDependent).length,
    familyMembersList,
    housingStatus:
      address?.residential_owner_type?.residential_ownership_type_name ||
      address?.ResidentialOwnerType?.residential_ownership_type_name ||
      "N/A",
    fullAddress: fullAddress || "N/A",
    attendancePresent:
      emp?.current_month_attendance_present != null
        ? Number(emp.current_month_attendance_present)
        : null,
    attendanceWorking:
      emp?.current_month_shift_workdays != null
        ? Number(emp.current_month_shift_workdays)
        : null,
    attendanceMonthLabel: emp?.current_month_label || "",
    attendanceSummary:
      emp?.current_month_attendance_present != null &&
      emp?.current_month_shift_workdays != null
        ? `${emp.current_month_attendance_present} / ${emp.current_month_shift_workdays}`
        : "N/A",
    leaves: leavesLabel,
    currentMonthLeaveDays,
    functionalRole: functionalRoleName,
    functionalSpecialization,
    functionalRolesList,
    organization: organizationName,
    division:
      emp?.businessdivision?.business_division_name ||
      emp?.business_division?.business_division_name ||
      "N/A",
    isClockedIn,
    isClockedOut,
  }
}

export default function EmployeeProfile() {
  const location = useLocation()
  const search = (location && location.search) || (typeof window !== "undefined" ? window.location.search : "")
  const params = new URLSearchParams(search)

  const navState = location?.state ?? {}
  const urlId = params.get("id")
  const urlPage = Number(params.get("page"))
  const urlIndex = Number(params.get("index"))
  const urlPageSize = Number(params.get("pageSize"))

  const getStored = (key, def) => {
    if (typeof window === "undefined") return def
    const v = window.localStorage.getItem(key)
    const n = v ? Number.parseInt(v, 10) : Number.NaN
    return Number.isFinite(n) ? n : def
  }

  const targetEmployeeId =
    urlId ??
    (typeof window !== "undefined" ? window.localStorage.getItem("selectedEmployeeId") : null) ??
    null

  const getEmployeeCode = (emp) => {
    if (!emp) return null
    return (
      emp.employee_code ??
      emp.emp_code ??
      emp.code ??
      emp.org_persons?.person_code ??
      emp.org_persons?.person?.person_code ??
      null
    )
  }

  const [apiUrl, setApiUrl] = useState(null)
  const [employeeList, setEmployeeList] = useState([])
  const [employee, setEmployee] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const [currentPage, setCurrentPage] = useState(() => {
    if (Number.isFinite(urlPage) && urlPage > 0) return urlPage
    if (typeof navState?.page === "number" && navState.page > 0) return navState.page
    return getStored("employeeCurrentPage", 1)
  })

  const [currentEmployeeIndex, setCurrentEmployeeIndex] = useState(() => {
    if (Number.isFinite(urlIndex) && urlIndex >= 0) return urlIndex
    if (typeof navState?.index === "number" && navState.index >= 0) return navState.index
    return getStored("employeeCurrentIndex", 0)
  })

  const [totalPages, setTotalPages] = useState(1)
  const [totalEmployees, setTotalEmployees] = useState(0)

  const PAGE_SIZE =
    (Number.isFinite(urlPageSize) && urlPageSize > 0
      ? urlPageSize
      : typeof navState?.pageSize === "number" && navState.pageSize > 0
        ? navState.pageSize
        : typeof window !== "undefined"
          ? Number.parseInt(localStorage.getItem("employeePageSize") || "", 10)
          : Number.NaN) || 10

  const { userData } = useAuthStore()
  const selectedEmployee = navState?.employee
  const passedEmployees = Array.isArray(navState?.employees) ? navState.employees : null

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("employeeCurrentPage", String(currentPage))
    }
  }, [currentPage])

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("employeeCurrentIndex", String(currentEmployeeIndex))
      if (selectedEmployee) {
        const id =
          selectedEmployee?.organization_person_id ??
          selectedEmployee?.employee_id ??
          selectedEmployee?.id ??
          selectedEmployee?.employeeId ??
          selectedEmployee?.emp_code ??
          selectedEmployee?.code
        if (id != null) localStorage.setItem("selectedEmployeeId", String(id))
      }
    }
  }, [currentEmployeeIndex, selectedEmployee])

  const navAppliedRef = useRef(false)

  useEffect(() => {
    if (navAppliedRef.current) return

    if (selectedEmployee) {
      if (passedEmployees?.length) {
        const safeIdx =
          typeof navState?.index === "number" && navState.index >= 0
            ? Math.min(navState.index, passedEmployees?.length - 1)
            : 0
        setEmployeeList(passedEmployees)
        setCurrentEmployeeIndex(safeIdx)
        setEmployee(mapEmployeeData(passedEmployees[safeIdx], userData))
        const tc = typeof navState?.totalCount === "number" ? navState.totalCount : passedEmployees?.length
        const tp =
          typeof navState?.totalPages === "number" ? navState.totalPages : Math.max(1, Math.ceil(tc / PAGE_SIZE))
        setTotalEmployees(tc)
        setTotalPages(tp)
        navAppliedRef.current = true
        setLoading(false)
        return
      }
      setEmployeeList([selectedEmployee])
      setCurrentEmployeeIndex(0)
      setEmployee(mapEmployeeData(selectedEmployee, userData))
      setTotalEmployees(typeof navState?.totalCount === "number" ? navState.totalCount : 1)
      setTotalPages(typeof navState?.totalPages === "number" ? navState.totalPages : 1)
      navAppliedRef.current = true
      setLoading(false)
      return
    }

    navAppliedRef.current = true
  }, [
    selectedEmployee,
    passedEmployees,
    navState?.index,
    navState?.totalCount,
    navState?.totalPages,
    PAGE_SIZE,
    userData,
  ])

  useEffect(() => {
    const orgId = resolveOrganizationId(userData)
    if (!orgId) return
    setApiUrl(`${MAIN_URL}/api/organization/${orgId}/employee-all`)
  }, [userData])

  useEffect(() => {
    if (!apiUrl) return
    if (passedEmployees?.length && navState?.page === currentPage) return

    const getId = (e) =>
      e?.organization_person_id ?? e?.employee_id ?? e?.id ?? e?.employeeId ?? e?.emp_code ?? e?.code ?? null

    const fetchEmployees = async () => {
      setLoading(true)
      try {
        const res = await fetch(apiUrl, { headers: getApiHeaders() })
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`)

        const data = await res.json()
        let employees = Array.isArray(data) ? data : data.employees || data.data || []
        const totalCount = data.totalCount || employees.length || 0

        employees = [...employees].sort((a, b) => {
          const idA = a.organization_person_id || a.employee_id || a.id || 0
          const idB = b.organization_person_id || b.employee_id || b.id || 0
          return idB - idA
        })

        setEmployeeList(employees)
        setTotalPages(Math.max(1, Math.ceil(totalCount / PAGE_SIZE)))
        setTotalEmployees(totalCount || employees?.length)

        let idx = 0
        if (targetEmployeeId) {
          const foundIndex = employees.findIndex((e) => {
            const empId = getId(e)
            return empId !== null && String(empId) === String(targetEmployeeId)
          })
          if (foundIndex >= 0) idx = foundIndex
        }
        setCurrentEmployeeIndex(idx)
        const chosen = employees[idx] || employees[0]
        if (chosen) setEmployee(mapEmployeeData(chosen, userData))
      } catch (err) {
        setError(err?.message || "An error occurred")
      } finally {
        setLoading(false)
      }
    }

    fetchEmployees()
  }, [apiUrl, passedEmployees, navState?.page, PAGE_SIZE, targetEmployeeId, userData, currentPage])

  useEffect(() => {
    if (employeeList?.length > 0 && currentEmployeeIndex >= 0 && currentEmployeeIndex < employeeList?.length) {
      setEmployee(mapEmployeeData(employeeList[currentEmployeeIndex], userData))
    }
  }, [currentEmployeeIndex, employeeList, userData])

  const handleEmployeeNavigation = (direction) => {
    if (employeeList?.length === 0) return

    const currentEmp = employeeList[currentEmployeeIndex]
    const currentEmpCode = getEmployeeCode(currentEmp)

    if (currentEmpCode) {
      const sortedEmployees = [...employeeList].sort((a, b) => {
        const codeA = getEmployeeCode(a)
        const codeB = getEmployeeCode(b)
        const numA = Number(codeA)
        const numB = Number(codeB)
        if (!isNaN(numA) && !isNaN(numB)) return numA - numB
        return String(codeA).localeCompare(String(codeB))
      })

      const currentSortedIndex = sortedEmployees.findIndex(
        (emp) => String(getEmployeeCode(emp)) === String(currentEmpCode)
      )

      if (currentSortedIndex >= 0) {
        let nextSortedIndex = -1
        if (direction === "next" && currentSortedIndex < sortedEmployees.length - 1) {
          nextSortedIndex = currentSortedIndex + 1
        } else if (direction === "prev" && currentSortedIndex > 0) {
          nextSortedIndex = currentSortedIndex - 1
        }

        if (nextSortedIndex >= 0) {
          const nextEmployee = sortedEmployees[nextSortedIndex]
          const nextEmployeeId = nextEmployee?.employee_id || nextEmployee?.id || nextEmployee?.employeeId
          const nextArrayIndex = employeeList.findIndex((emp) => {
            const empId = emp?.employee_id || emp?.id || emp?.employeeId
            return empId === nextEmployeeId
          })
          if (nextArrayIndex >= 0) {
            setCurrentEmployeeIndex(nextArrayIndex)
            return
          }
        }
      }
    }

    if (direction === "next") {
      if (currentEmployeeIndex < employeeList.length - 1) {
        setCurrentEmployeeIndex(currentEmployeeIndex + 1)
      } else if (currentPage < totalPages) {
        setCurrentPage(currentPage + 1)
        setCurrentEmployeeIndex(0)
      }
    } else if (currentEmployeeIndex > 0) {
      setCurrentEmployeeIndex(currentEmployeeIndex - 1)
    } else if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
      setCurrentEmployeeIndex(PAGE_SIZE - 1)
    }
  }

  const getCurrentEmployeeNumber = () => {
    if (employeeList?.length > 0 && currentEmployeeIndex >= 0 && currentEmployeeIndex < employeeList.length) {
      const currentEmpCode = getEmployeeCode(employeeList[currentEmployeeIndex])
      if (currentEmpCode) {
        const sortedEmployeeCodes = employeeList
          .map((emp) => getEmployeeCode(emp))
          .filter((code) => code != null)
          .sort((a, b) => {
            const numA = Number(a)
            const numB = Number(b)
            if (!isNaN(numA) && !isNaN(numB)) return numA - numB
            return String(a).localeCompare(String(b))
          })
        const position = sortedEmployeeCodes.findIndex((code) => String(code) === String(currentEmpCode))
        return position >= 0 ? position + 1 : (currentPage - 1) * PAGE_SIZE + currentEmployeeIndex + 1
      }
    }
    return (currentPage - 1) * PAGE_SIZE + currentEmployeeIndex + 1
  }

  const canNavigateNext = () => {
    if (employeeList?.length === 0) return false
    const currentEmpCode = getEmployeeCode(employeeList[currentEmployeeIndex])
    if (currentEmpCode) {
      const sortedEmployeeCodes = employeeList
        .map((emp) => getEmployeeCode(emp))
        .filter((code) => code != null)
        .sort((a, b) => {
          const numA = Number(a)
          const numB = Number(b)
          if (!isNaN(numA) && !isNaN(numB)) return numA - numB
          return String(a).localeCompare(String(b))
        })
      const currentPosition = sortedEmployeeCodes.findIndex((code) => String(code) === String(currentEmpCode))
      return currentPosition >= 0 && currentPosition < sortedEmployeeCodes.length - 1
    }
    return currentEmployeeIndex < employeeList.length - 1 || currentPage < totalPages
  }

  const canNavigatePrev = () => {
    if (employeeList?.length === 0) return false
    const currentEmpCode = getEmployeeCode(employeeList[currentEmployeeIndex])
    if (currentEmpCode) {
      const sortedEmployeeCodes = employeeList
        .map((emp) => getEmployeeCode(emp))
        .filter((code) => code != null)
        .sort((a, b) => {
          const numA = Number(a)
          const numB = Number(b)
          if (!isNaN(numA) && !isNaN(numB)) return numA - numB
          return String(a).localeCompare(String(b))
        })
      const currentPosition = sortedEmployeeCodes.findIndex((code) => String(code) === String(currentEmpCode))
      return currentPosition > 0
    }
    return currentEmployeeIndex > 0 || currentPage > 1
  }

  if (loading) {
    return (
      <div className="ems-ep-page ems-fade-up">
        <div className="ems-ep-empty">Loading employee snapshot…</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="ems-ep-page ems-fade-up">
        <div className="ems-ep-empty error">Error: {error}</div>
      </div>
    )
  }

  if (!employee) {
    return (
      <div className="ems-ep-page ems-fade-up">
        <div className="ems-ep-empty">No employee data found</div>
      </div>
    )
  }

  return (
    <div className="ems-ep-page ems-fade-up">
      <div className="ems-ep-shell">
        <div className="ems-page-head">
          <div>
            <h1 className="ems-page-title">Employee Profile</h1>
            <div className="ems-page-sub">
              <span>People</span>
              <span className="ems-dot" />
              <span>{display(employee.name, "Employee details")}</span>
            </div>
          </div>
          <EmployeePagination
            currentEmployee={getCurrentEmployeeNumber()}
            totalEmployees={Math.max(totalEmployees, employeeList?.length)}
            onPrevious={() => handleEmployeeNavigation("prev")}
            onNext={() => handleEmployeeNavigation("next")}
            canNavigatePrev={canNavigatePrev()}
            canNavigateNext={canNavigateNext()}
          />
        </div>

        <div className="ems-ep-hero">
          <ProfileImageCard
            imageUrl={employee.imageUrl}
            name={employee.name}
            designation={employee.designation}
            employeeCode={employee.employeeCode}
          />
          <SnapshotHeroCard employee={employee} />
        </div>

        <div className="ems-ep-grid">
          <PersonalEducationCard employee={employee} />
          <FamilyDetailsCard employee={employee} />
          <AttendanceCard employee={employee} />
          <FunctionalRoleCard employee={employee} />
        </div>
      </div>
    </div>
  )
}
